/* ⚠️ GENERATED from app/services/feedPaging.ts by gen-twins.mjs — edit the TypeScript, not this. */
// ════════════════════════════════════════════════════════════════════════
// services/feedPaging.ts — when to ask for the next ranked page, and what to
// keep from it.
//
// 🟥 THIS IS TWO LINES OF POLICY THAT DECIDE WHETHER THE FEED ENDS. A ranked
//    page is twenty posts; the query it replaced was fifty. Get the "ask
//    early" rule wrong and the reader waits on a round trip between two
//    posts; get the "already have it" rule wrong and the server hands back a
//    page it has already given and the feed appears to loop. Neither failure
//    throws, neither is visible in a log, and both read as "the algorithm
//    broke my feed" rather than as a missing fetch.
//
// 🟢 SO IT LIVES IN ITS OWN FILE, testable by execution, and the browser has
//    the same two functions in allo-web/src/services/feedPaging.js. One rule,
//    one home — the phone and the browser end their feeds the same way or
//    they are two products.
// ════════════════════════════════════════════════════════════════════════

/**
 * Should we go and get more?
 *
 * 🟥 ASKED FOR EARLY, NOT WHEN EMPTY. Waiting until nothing is left puts a
 *    network round trip between a scroll and the next post — on the connections
 *    where that hurts most, it is the difference between a feed and a stutter.
 *    Two batches of headroom is enough for the page to land unnoticed.
 */
export function shouldFetchMore(s) {
  if (!s || !s.ranked) return false;
  if (s.inFlight || s.exhausted) return false;
  const batch = Math.max(1, Number(s.batchSize) || 8);
  return (Number(s.remaining) || 0) <= batch * 2;
}

/**
 * What survives from a page that just arrived.
 *
 * 🟥 `have` IS EVERYTHING ALREADY FETCHED, NOT WHAT IS ON SCREEN. The list
 *    holds posts that have been fetched and not yet scrolled to; deduping only
 *    against the visible ones lets the same posts come back a second time.
 *
 * 🟥 AND THE BLOCK AND HIDE LISTS ARE APPLIED AGAIN HERE. The server applies
 *    them when it builds a page, but it read them a moment ago — a block made
 *    since is only known on this device. Being shown somebody you just blocked
 *    is the one failure a feed never gets forgiven for.
 *
 * 🟢 `exhausted` IS RETURNED, NOT INFERRED BY THE CALLER. "Nothing new came
 *    back" is the only honest end of a feed, and it has to be remembered or
 *    every one of the next fifty scroll events asks again.
 */
export function mergeNewPage(args) {
  const have = new Set(args?.have || []);
  const blocked = new Set(args?.blocked || []);
  const hidden = new Set(args?.hidden || []);
  const fresh = [];
  for (const p of args?.incoming || []) {
    if (!p || !p.id) continue;
    if (have.has(p.id) || hidden.has(p.id)) continue;
    if (p.userId && blocked.has(p.userId)) continue;
    have.add(p.id); // a page that repeats itself is deduped too
    fresh.push(p);
  }
  return {
    fresh,
    exhausted: fresh.length === 0
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 A PULL-TO-REFRESH IS A RESTART, AND IT WAS BEING SENT AS THE NEXT PAGE.

   The refresh handler re-ran the same fetch the first load runs — and that
   fetch passes every post id already shown this session as `exclude`. So
   "refresh" asked the server for a page of things the reader has NOT seen,
   which on an account that has read most of what it is eligible for is an empty
   page. The client read empty as "the ranker is off", fell back to
   `posts orderBy timestamp limit 50` — the same posts already on screen — and
   set `ranked = false` for the rest of the session, so paging never recovered.

   Three faults stacked into one silent symptom: refresh changed nothing, and
   the feed quietly stopped being ranked.

   🟢 SO THE REQUEST SAYS WHICH IT IS. Paging excludes what it has; a refresh
      excludes nothing and asks the server to relax the seen ring. Repeating a
      post somebody asked to see again is not a failure; refusing to show them
      anything is.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How much of the list counts as "in front of the reader" on a refresh. */
export const ON_SCREEN = 60;

/**
 * What to ask the ranker for.
 *
 * 🟥 THE LAST 200, NOT THE FIRST. The caller sent `slice(0, 200)` — the OLDEST
 *    ids it held — so the exclusions guarded against repeating what you saw an
 *    hour ago and did nothing about what you had just scrolled past.
 */
export function pageRequest(mode, have) {
  const ids = (have || []).filter(Boolean);
  /* 🟥 A REFRESH THAT EXCLUDED NOTHING RETURNED THE SAME PAGE, EVERY TIME.
     "it pulls to refresh but the posts do not change."
      The ranker is a pure function of the corpus and the profile, so asking it
     the same question twice within a second gets the same answer twice — and
     `exclude: []` was literally the same question. The seen ring was the only
     thing that had made the answer differ, and a refresh switches it off.
      🟢 SO A REFRESH SAYS WHAT IS ON THE SCREEN. Not "what I have ever read" —
        that is the ring, and it stays relaxed, because an account that has read
        everything must still get a page. Just the posts in front of the reader
        right now, which is the one set that must not come back unchanged.
      🟢 AND THE SERVER TREATS IT AS A DEMOTION, NOT A BAN (feedService), so on a
        thin corpus the page refills with what was on it rather than running
        short. A refresh is allowed to repeat; it is not allowed to do nothing. */
  if (mode === 'refresh') return {
    exclude: ids.slice(0, ON_SCREEN),
    refresh: true
  };
  return {
    exclude: ids.slice(-200),
    refresh: false
  };
}

/**
 * Does this answer mean the caller should take the feed back?
 *
 * 🟥 THE OLD TEST WAS `!page`, AND `!page` WAS TRUE OF AN EMPTY RANKED PAGE.
 *    That is the line that turned "nothing new right now" into "the ranker is
 *    switched off", threw away the ranking, and re-queried by timestamp.
 */
export function shouldFallBack(result) {
  return !result || result.ranked !== true;
}

/* ═══════════════════════════════════════════════════════════════════════════
   📚 WHEN THE RANKER HAS NOTHING MORE, THE FEED STILL HAS MORE.

   🟥 "MY FEED STOPS — AND THE PLATFORM HAS MORE POSTS." Both readings of that
      were true at once. The ranker only ever looks at ~440 candidates (a few
      capped pools), and paging hard-drops the last four hundred posts this
      account was shown — so on a corpus somebody reads every day, the ranked
      pages run dry long before the posts do. An empty page was then the end of
      the feed. And when the ranker was off (the kill switch, an error), the
      feed was the fifty newest posts with no next page at all.

   🟥 REELS RAN OUT FOR THE SAME REASON, FASTER. The reels surface is the
      videos inside those same pools, so the player showed the videos already
      in the feed, asked for more, got none, and ended.

   🟢 SO THE FEED CONTINUES INTO THE ARCHIVE. When the ranker says it has
      nothing more — or is not ranking at all — the next posts are simply the
      next older ones, newest first, page by page, until the collection itself
      ends. Everything the feed already holds is skipped, and blocks, hidden
      posts and friends-only audiences are applied exactly as they are to a
      ranked page. Reels take the videos from the same walk.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How many posts one archive read asks for (a single-field order — no index needed). */
export const ARCHIVE_PAGE = 40;
/** How many archive reads one "load more" may make looking for something new. */
export const ARCHIVE_MAX_READS = 4;
/**
 * Where the next posts come from.
 *
 * 🟥 AN EXHAUSTED RANKER IS NOT THE END OF THE FEED, and an unranked feed is
 *    not a feed of fifty. Only the archive reaching the oldest post is.
 */
export function feedSource(s) {
  if (s && s.ranked && !s.rankedExhausted) return 'ranked';
  if (s && s.archiveEnded) return 'none';
  return 'archive';
}

/** Is this post a video (the main clip, or any clip in a carousel)? */
export function isVideoPost(p) {
  if (!p) return false;
  if (p.mediaType === 'video' || !!p.videoUrl) return true;
  return Array.isArray(p.media) && p.media.some(m => m && m.type === 'video');
}

/**
 * What one archive read contributes.
 *
 * 🟢 `ended` IS "THIS READ CAME BACK SHORT" — the collection has nothing older.
 *    A read whose posts were all already in hand is NOT the end; the next read
 *    goes further back.
 *
 * 🟥 AND THE AUDIENCE IS APPLIED HERE, the same rule the server applies to a
 *    ranked page: a friends-only post reaches its author and their friends,
 *    and nobody else, however it was found.
 */
export function archivePick(args) {
  const incoming = Array.isArray(args?.incoming) ? args.incoming : [];
  const friends = new Set(args?.friends || []);
  const me = args?.me || '';
  const visible = incoming.filter(p => {
    if (!p) return false;
    if (p.audience === 'friends' && p.userId !== me && !friends.has(p.userId)) return false;
    if (args?.videoOnly && !isVideoPost(p)) return false;
    return true;
  });
  const {
    fresh
  } = mergeNewPage({
    have: args?.have || [],
    incoming: visible,
    blocked: args?.blocked,
    hidden: args?.hidden
  });
  return {
    fresh,
    ended: incoming.length < Math.max(1, Number(args?.pageSize) || ARCHIVE_PAGE)
  };
}

/* 🟥 A REFRESH OF THE UNRANKED FEED SHOWED THE SAME EIGHT POSTS. Its local
   score is freshness + engagement + a little randomness; on a feed of older,
   popular posts the engagement term wins every time, so pulling down re-sorted
   the same fifty into the same top eight. The ranked feed solved this on the
   server by demoting what is on the screen; this is the same rule for the
   local scorer. Big enough to sink a post below anything not on screen, never
   a filter — on a thin feed they come back, just not first. */
export const REFRESH_ON_SCREEN_PENALTY = 2000;
export function refreshPenalty(id, onScreen) {
  if (!id || !onScreen) return 0;
  const has = onScreen instanceof Set ? onScreen.has(id) : onScreen.includes(id);
  return has ? -REFRESH_ON_SCREEN_PENALTY : 0;
}

/* ═══ 🎬 THE REELS PLAYER ═══════════════════════════════════════════════════
   "When I open a video the reels open in the order it was posted, not by the
    algorithm — and scrolling up just walks the main feed's videos. And with one
    video in my feed it says 'no more videos, back to Vibes' while the platform
    has plenty."

   🟥 THE PLAYER WAS SEEDED WITH EVERY VIDEO IN THE MAIN FEED, in the main feed's
      order, opened at the tapped one. So the reels next to it — above AND below —
      were the feed's, ordered by the feed's ranking (or by date when the feed had
      fallen back), and the reels ranking only ever got to fill in behind them.
   🟢 reelsSeed: the tapped clip, alone. It plays at once, as before, and every
      reel after it comes from the reels ranking; there is nothing above it to
      scroll back into.

   🟥 AND THE PLAYER ONLY ASKED THE REELS RANKER WHEN THE MAIN FEED HAD BEEN
      RANKED. A feed that fell back to the unranked path sent the player straight
      to the archive — newest first: "the order it was posted". The reels surface
      is its own question; it is asked every session (feedSource ranked: true).

   🟥 "NO MORE VIDEOS" WAS A FOOTER THAT WAS ALWAYS THERE. With one video in hand
      the first swipe landed on it while the first page was still in flight. And
      an archive read that found no video in its few pages (a stretch of photo
      posts) returned nothing without having ended — no new rows, so the pager
      never asked again. readUntilSome reads on until a clip turns up or the
      archive genuinely ends, and reelsAtEnd is the only thing allowed to say
      "caught up"; until then the end of the pager is a loading slide. */

/** The player opens on the tapped clip and nothing else — the rest is ranked. */
export function reelsSeed(clicked) {
  return clicked && clicked.id ? [clicked] : [];
}

/** archive rounds one "next reels" request may spend looking for a clip */
export const REELS_MAX_ROUNDS = 8;

/** Call `read` until it returns something or `ended()` says there is nothing left. */
export async function readUntilSome(read, ended, rounds = REELS_MAX_ROUNDS) {
  for (let i = 0; i < Math.max(1, rounds); i += 1) {
    const got = await read();
    if (Array.isArray(got) && got.length || ended()) return Array.isArray(got) ? got : [];
  }
  return [];
}

/** "Caught up" — only when the ranker AND the archive have both run out. */
export function reelsAtEnd(s) {
  return !!(s && s.rankedExhausted && s.archiveEnded && !s.inFlight);
}
